import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  useDate,
  useOrderByFilter,
  usePagination,
  useToQueryString,
  statusesIds,
} from '@/core'
import type { TDailyReport, TProject } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination, TRecordAction } from '@/core/types'
import { useCatalogStore } from '@/core/store/catalogs'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const form = ref<TProject>({})
  const formReport = ref<TDailyReport>({})
  const store = useCatalogStore()
  const { catalogs } = storeToRefs(store)
  const route = useRoute()
  const { zonedTimeToUtc } = useDate()

  const filters = ref<ToQueryStringFilters>({
    param: '',
    dates: {
      created_at: [],
    },
    status_id: [statusesIds['in-production']],
    order_by: 'created_at,desc',
  })

  const customerFilters = ref<ToQueryStringFilters>({
    param: '',
    name: '',
    order_by: 'created_at,desc',
  })

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: ['id', 'name'],
  })
  const customerQueryParams = useToQueryString(toReactive(customerFilters), {
    searchColumns: ['id', 'name'],
  })

  const paginationModule = usePagination()

  const moduleQuery = useModuleQuery({
    modelId: computed(() => route.params?.id?.toString()),
    currentPage: paginationModule.currentPage,
    filters: {
      loadRecords: moduleQueryParams.queryParams,
      customersCatalog: customerQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: true,
      customersCatalog: true,
      loadRecord: computed(() => !!route.params?.id?.toString()),
      loadHeaders: true,
      loadBundlesFabricated: computed(() => !!route.params?.id?.toString()),
      loadUsedCoils: computed(() => !!route.params?.id?.toString()),
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        paginationModule.setPagination(pagination)
      },
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at'],
  })

  const onAdd = async (payload: TProject) => {
    await moduleQuery.addRecord.mutateAsync({
      ...payload,
      delivery_deadline: zonedTimeToUtc(
        new Date(payload.delivery_deadline ?? ''),
        {
          removeTimeZone: true,
        },
      ),
    })
  }

  const onEdit = async (payload: TProject) => {
    await moduleQuery.editRecord.mutateAsync({
      ...payload,
      delivery_deadline: zonedTimeToUtc(
        new Date(payload.delivery_deadline ?? ''),
        {
          removeTimeZone: true,
        },
      ),
    })
  }

  const onDelete = async (payload: TProject) => {
    await moduleQuery.deleteRecord.mutateAsync(payload)
  }

  const onAction = async (action: TRecordAction) => {
    await moduleQuery.actionRecord.mutateAsync(action)
  }

  const onRequestCatalog = async (payload: number[]) => {
    await moduleQuery.requestCatalog.mutateAsync({ ids: payload })
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)
  const tableBundles = computed(
    () => moduleQuery.loadBundlesFabricated.data?.value?.data,
  )
  const tableCoils = computed(() => moduleQuery.loadUsedCoils.data?.value?.data)
  const model = computed(() => moduleQuery.loadRecord?.data?.value)
  const headers = computed(() => moduleQuery.loadHeaders?.data?.value)
  const isLoading = computed(
    () =>
      moduleQuery.addRecord.isLoading.value ||
      moduleQuery.editRecord.isLoading.value ||
      moduleQuery.loadRecords.isLoading.value,
  )

  const isLoadingCategories = computed(
    () => moduleQuery.requestCatalog.isLoading.value,
  )
  const customers = computed(() => moduleQuery.customersCatalog.data?.value)
  const customersIsLoading = computed(
    () => moduleQuery.customersCatalog.isLoading.value,
  )

  return {
    tableData,
    filters,
    orderByOptions,
    rows,
    form,
    formReport,
    model,
    isLoading,
    isLoadingCategories,
    catalogs,
    customersIsLoading,
    customers,
    customerFilters,
    tableBundles,
    tableCoils,
    headers,
    modulePagination: toReactive(paginationModule),
    onRequestCatalog,
    onAction,
    onDelete,
    onEdit,
    onAdd,
  }
})

export { useProvideModule, useModule }
